<template>
    <el-tree
            ref="tree"
            id="tree-analysis"
            :data="treeData"
            node-key="Id"
            :props="$options.treeProps"
            :filter-node-method="filterNode"
            empty-text="No matches"
            v-loading="loading"
            highlight-current
            default-expand-all
            :expand-on-click-node="false"
            @node-click="onNodeClick"
            @node-contextmenu="onNodeContextmenu">
        <div class="custom-tree-node no-select" slot-scope="{ node, data }">
            <!--NOTE: Changes made during development may require a page refresh.-->
            <dh-icon v-if="data.icon" class="type-icon" :name="data.icon"/>
            <span :id="data.Id" class="node-label" @dblclick="onNodeDblClick(data)">
                {{ data.label }}
            </span>
        </div>
    </el-tree>
    <!--NOTE: It is not possible to use ContextMenu here, so props and Vuex need to be used.-->
</template>

<script>
  import {sortByString} from "../../assets/js/utils";

  export default {
    name: "TreeAnalysis",
    props: {
      filterNode: Function,
    },
    treeProps: {
      label: 'label',
      children: 'children',
    },

    data() {
      return {
        // APOLLO
        // configs: [],
        // dutyCycles: [],

        // TREE
        treeData: [{
          Id: 1,
          label: 'Configurations',
          children: [],
        }, {
          Id: 2,
          label: 'Duty Cycles',
          children: [],
        }, {
          Id: 3,
          label: 'Powerflows',
          children: [],
        }],
      }
    },
    computed: {
      loading() {
        return this.$apollo.queries.configs.loading || this.$apollo.queries.dutyCycles.loading;
      },
      analyzeLoadCase() {
        return this.$store.state.ui.analyze.loadCase;
      },
    },
    watch: {
      analyzeLoadCase(newVal, oldVal) {
        // remove any existing highlight, then apply new highlight
        if (oldVal) document.getElementById(oldVal.Id).style.fontWeight = 'revert';
        if (newVal) document.getElementById(newVal.Id).style.fontWeight = 'bold';
      }
    },
    apollo: {
      configs: {
        manual: true,
        fetchPolicy: 'cache-first',
        skip() {return typeof this.$store.state.model.doc.Id === 'undefined'},
        variables() {return {Id: this.$store.state.model.doc.Id}},
        query() {return this.$store.state.model.getConfigsQuery},
        result({data: {gearsModel: {configs}}}) {
          // Map configs to tree nodes.
          // console.log('TreeAnalysis configs', configs);

          this.treeData[0].children = configs.map(config => {
            return {
              __typename: config.__typename,
              Id: config.Id,
              label: config.name,
              icon: 'el-icon-s-operation'
            };
          });
        },
      },
      dutyCycles: {
        manual: true,
        fetchPolicy: 'cache-first',
        skip() {return typeof this.$store.state.model.doc.Id === 'undefined'},
        variables() {return {model: this.$store.state.model.doc.Id}},
        query() {return this.$requests.model.getDutyCycles},
        result({data: {dutyCycles}}) {
          // Map duty cycles, load cases, and results to tree nodes.
          // console.log('TreeAnalysis dutyCycles', dutyCycles);

          // initialize
          this.treeData[1].children = [];
          this.treeData[2].children = [];

          dutyCycles.forEach(dc => {
            const dcNode = {
              ...dc,
              label: dc.name,
              icon: this.$icons.tableGrid
            };

            // duty cycle tree
            this.treeData[1].children.push(dcNode);

            // powerflow tree; DC > LC:powerflow
            this.treeData[2].children.push({
              ...dcNode,
              showMenuPowerflow: true,  // contextmenu
              children: dc.loadCases.map(lc => {
                return {
                  ...lc,
                  dutyCycle: {Id: dc.Id},
                  label: lc.name,
                  icon: lc.powerflow ? this.$icons.powerflow : 'el-icon-close',
                }
              }),
            });
          });
          sortByString(this.treeData[1].children, 'label');
        },
      },
    },

    mounted() {
      this.$refs['tree'].$el.addEventListener('keydown', this.onTreeKeydown, false);
    },
    methods: {
      setSelected(data) {
        // Helper function for left and right click handlers.
        // Set global state for selected config, duty cycle, or powerflow.

        switch (data.__typename) {
          case this.$requests.model.TYPENAME.CONFIG:
            // globally select a config
            this.$store.commit('SET_SELECTED_CONFIG', data);
            break;
          case this.$requests.model.TYPENAME.DUTY_CYCLE: {
            // globally select a duty cycle
            // eslint-disable-next-line no-unused-vars
            const {children, ...dutyCycle} = data;
            this.$store.commit('SET_SELECTED_DUTY_CYCLE', dutyCycle);
            break;
          }
          case this.$requests.model.TYPENAME.LOAD_CASE: {
            // globally select a powerflow result
            this.$store.commit('SET_SELECTED_LOAD_CASE', data);
            break;
          }
        }
      },

      // EVENTS
      onNodeClick(data) {
        // args: data, TreeNode.node, TreeNode
        // Hide any existing contextmenu.

        this.setSelected(data);
        this.$store.commit('HIDE_CONTEXTMENU');
      },
      onNodeContextmenu(event, data) {
        // args: event, data, TreeNode.node, TreeNode
        // Note that top-level tree branches do not have a __typename, hence checks node.__typename.
        // Do not call handleLeftClick(), as it will go against the native selection behavior.

        // set tree node
        this.$refs.tree.setCurrentNode(data);

        // set selected state
        this.setSelected(data);

        // show context menu
        if (data.__typename) {
          this.$store.dispatch('showContextMenu', {event, type: data.__typename})
        }
      },
      onNodeDblClick(data) {
        // Open views to edit analysis features.

        switch (data.__typename) {
          case this.$requests.model.TYPENAME.CONFIG:
            this.$store.commit('SET_EDIT_CONFIG', data);
            this.$store.commit('TOGGLE_DIALOG_VISIBILITY', 'DialogSetupConfig');
            break;
          case this.$requests.model.TYPENAME.DUTY_CYCLE:
            this.$store.commit('TOGGLE_DIALOG_VISIBILITY', 'DialogSetupLoadCases');
            break;
          case this.$requests.model.TYPENAME.LOAD_CASE:
            this.$store.dispatch('setLoadCase', data);
            break;
        }
      },
      onTreeKeydown(event) {
        switch (event.key) {
          case 'Enter':
            this.onNodeDblClick(this.$refs.tree.getCurrentNode());
            break;
          case 'Delete': {
            const data = this.$refs.tree.getCurrentNode();
            switch (data.__typename) {
              case this.$requests.model.TYPENAME.CONFIG:
                return this.$store.dispatch('removeGearsConfig', data.Id);
              case this.$requests.model.TYPENAME.DUTY_CYCLE:
                return this.$store.dispatch('removeDutyCycle', data.Id);
            }
            break;
          }
          case 'Esc': // IE/Edge specific value
          case 'Escape':
            this.$store.dispatch('clearUI');
            this.$store.commit('CLEAR_STATUS');
            break;
        }
      },
    },
  }
</script>

<style scoped lang="scss">
    #tree-analysis {
        min-width: 100%;  /* ensures tree node highlights across full width of browser tab if content is less wide */
        width: fit-content;  /* ensures proper width for long content, thus triggering the browser overflow-x */
        font-size: var(--font-med);
    }
    .custom-tree-node {
        font-size: var(--font-med-small);
        padding-right: 1em;

        /* DESIGNED FOR FULL HOTSPOT ON NODE LABEL */
        width: 100%;
        height: 100%;
        display: grid;
        align-items: center;
        grid-template-columns: auto 1fr;

        .type-icon {
            color: var(--color-secondary);
            padding-right: .4em;
        }
        .node-label {
            width: 100%;
            height: 100%;
            display: grid;
            align-items: center;
            text-align: left;
            /* TUNE HOTSPOT */
            /*background-color: lightgray;*/
        }
    }
</style>
